import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { ListResponse } from 'src/common/utils/api/client';
import { TableData } from './useTable';
import { useClientPagination } from './useClientPagination';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { BaseRecord, SortableColumn } from '../types';

export const useClientPaginatedTable = <T extends BaseRecord>(options: {
  resource: string;
  columns: SortableColumn<T>[];
  searchTerm: string;
  filterPredicate?: (item: T, search: string) => boolean;
  paginationDependencies?: unknown;
  filters?: Record<string, unknown>;
  pageSize: number;
  useLocalPagination: boolean;
  infinite?: boolean;
  enabled?: boolean;
}): TableData<T> => {
  const {
    resource,
    columns,
    searchTerm,
    filterPredicate,
    paginationDependencies,
    filters,
    pageSize = 10,
    useLocalPagination,
    infinite = false,
    enabled = true,
  } = options;

  const resourceSegments = resource.split('/');

  const {
    data: rawData,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery<T[] | ListResponse<T>, Error, T[]>({
    queryKey: filters ? [...resourceSegments, filters] : resourceSegments,
    select: data => (Array.isArray(data) ? data : (data.items ?? [])),
    enabled,
  });

  useEffect(() => {
    if (isError && error) {
      notifyAndLogError(
        'Failed to load data',
        'An error occurred while loading table data. Please try again later.',
        error,
        { resource, filters, pageSize },
      );
    }
  }, [isError, error, resource, filters, pageSize]);

  const allData = rawData ?? [];

  const { paginatedData, pagination, totalRecords, sortStatus, onSortStatusChange } =
    useClientPagination({
      data: allData,
      columns,
      searchTerm,
      filterPredicate,
      paginationDependencies,
      pageSize,
      useLocalPagination,
      infinite,
      enabled,
    });

  return {
    data: paginatedData,
    isLoading,
    isFetching,
    isError,
    error,
    pagination,
    totalRecords,
    refetch: () => {
      try {
        return refetch();
      } catch (error) {
        notifyAndLogError(
          'Refresh failed',
          'Failed to refresh table data. Please try again.',
          error,
          { resource },
        );
        throw error;
      }
    },
    sortStatus,
    onSortStatusChange,
  };
};
